import React, {useEffect, useState} from "react";
import {useDropzone} from "react-dropzone";
import {CircularProgress} from "@mui/material";
import {styled} from "@mui/material/styles";

import {COLOR_BLUE, COLOR_LIGHT_GREY} from "@/themes/colors.jsx";
import DefaultEmptyIcon from '@svg/image-icon-67.svg';
import CameraIcon from '@svg/camera-36.svg';
import ErrorLabel from "@/components/form/ErrorLabel.jsx";

const PhotoDropzone = function ({
                                    image,
                                    imageUrl,
                                    uploadText,
                                    borderRadius,
                                    onDrop,
                                    onRemove,
                                    error,
                                    removable,
                                    loading,
                                    emptyIcon = <DefaultEmptyIcon/>,
                                    ...props
                                }) {
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        if (image) {
            setPreviewImage(image);
        }
    }, [image, imageUrl]);

    const onDropAccepted = (acceptedFiles) => {
        onDrop(acceptedFiles)
    }

    const { getRootProps, getInputProps, open } = useDropzone({
        onDropAccepted,
        accept: {
            'image/png': [],
            'image/jpeg': [],  // Covers both .jpg and .jpeg
        },
        multiple: false,
    });

    const hasImage = () => {
        return !!previewImage;
    }

    return (
        <Container>
            <Error>{error}</Error>

            <DropzoneContainer
                {...getRootProps()}
                haveError={!!error}
                {...props}
            >
                <ImageContainer>
                    <PreviewContainer borderRadius={borderRadius}>
                        {hasImage() ? (
                            <PreviewImage src={previewImage} borderRadius={borderRadius}/>
                        ) : (
                            emptyIcon
                        )}
                        {loading && (
                            <LoadingContainer>
                                <CircularProgress size={25}/>
                            </LoadingContainer>
                        )}
                        <Camera/>
                    </PreviewContainer>

                    <AddPhotoLink
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            open();
                        }}
                    >
                        {hasImage() ? `Change ${uploadText}` : `Upload ${uploadText}`}
                    </AddPhotoLink>
                </ImageContainer>

                <DropzoneInput {...getInputProps()} />
            </DropzoneContainer>
        </Container>
    );
};

export default PhotoDropzone;

const Container = styled('div')({
    width: '100%',
    position: 'relative',
    textAlign: 'left',
});

const DropzoneContainer = styled('div')({
    width: '100%',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    outline: 'none',
    position: 'relative',
});

const DropzoneInput = styled('input')({
    width: '100%',
    aspectRatio: '1',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    outline: 'none',
});

const ImageContainer = styled('div')(({borderRadius}) => ({
    width: '108px',
    position: 'relative',
}));

const PreviewContainer = styled('div')(({borderRadius}) => ({
    width: '108px',
    height: '108px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: COLOR_LIGHT_GREY,
    borderRadius: borderRadius || '10px',
}));

const PreviewImage = styled('div')(({src, borderRadius}) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `url(${src})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    borderRadius: borderRadius || '10px',
}));

const Camera = styled(CameraIcon)({
    position: 'absolute',
    right: '-6px',
    bottom: '-6px',
});

const LoadingContainer = styled('div')({
    width: '100%',
    aspectRatio: '1',
    position: 'absolute',
    zIndex: 1,
    backgroundColor: 'rgba(242, 242, 242, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const AddPhotoLink = styled('div')({
    left: 0,
    cursor: 'pointer',
    display: 'block',
    paddingTop: '12px',
    fontSize: '13px',
    fontWeight: 800,
    lineHeight: '15.51px',
    textAlign: 'center',
    color: COLOR_BLUE,
});

const Error = styled(ErrorLabel)({
    position: 'absolute',
    top: '-15px',
    zIndex: 10,
    margin: 0,
    width: '100%',
});





